import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ScrollToTopButton from 'shared/components/ScrollToTopButton';
import { createSizes } from 'shared/helpers/createSizes';

function HowItWorksCTA() {
  const { t } = useTranslation('static_pages');
  return (
    <>
      <Box maxWidth="600px" mt={{ xs: 4, md: 6 }} textAlign="center" mx="auto">
        <Typography
          textAlign="center"
          variant="h3"
          component="p"
          mb={4}
          fontSize={{ xs: 24, md: 36 }}
          xs={{ lineHeight: 1.8 }}
        >
          {t('marketingPages.howItWorksCTA.title')}
        </Typography>
      </Box>
      <HowItWorksContent>
        <Item
          imageSrc="/artifacts/images/marketing_pages/sea/how_it_works/step1.webp"
          title={t('marketingPages.howItWorksCTA.section1.title')}
          description={t('marketingPages.howItWorksCTA.section1.content')}
        />
        <Item
          imageSrc="/artifacts/images/marketing_pages/sea/how_it_works/step2.webp"
          title={t('marketingPages.howItWorksCTA.section2.title')}
          description={t('marketingPages.howItWorksCTA.section2.content')}
        />
        <Item
          imageSrc="/artifacts/images/marketing_pages/sea/how_it_works/step3.webp"
          title={t('marketingPages.howItWorksCTA.section3.title')}
          description={t('marketingPages.howItWorksCTA.section3.content')}
        />
      </HowItWorksContent>
      <Box mb={1.5} pt={4} textAlign="center">
        <ScrollToTopButton>{t('common:labels.findCaterers')}</ScrollToTopButton>
      </Box>
      <Box>
        <Typography fontSize={14} textAlign="center">
          {t('common:labels.requestCaterersCTA')}
        </Typography>
      </Box>
    </>
  );
}

interface ItemProps {
  title: string;
  description: string;
  imageSrc: string;
}

const Item = (props: ItemProps) => {
  return (
    <HowItWorksItem>
      <ImageWrapper px={{ xs: 0, sm: 2, lg: 4 }}>
        <Image
          width={280}
          height={230}
          objectFit="cover"
          layout="responsive"
          src={props.imageSrc}
          sizes={createSizes({ xs: '300px', sm: '30vw' })}
          alt=""
        />
      </ImageWrapper>
      <Box px={2} flex={1} textAlign={{ sm: 'center' }} pt={{ sm: 4 }}>
        <Typography
          variant="h4"
          lineHeight="160%"
          fontSize={{ xs: 18, md: 20 }}
          pb={1}
        >
          {props.title}
        </Typography>

        <Typography variant="body2" lineHeight="168%">
          {props.description}
        </Typography>
      </Box>
    </HowItWorksItem>
  );
};

const ImageWrapper = styled(Box)`
  width: 100%;
  max-width: 300px;
  ${(props) => props.theme.breakpoints.up('sm')} {
    max-width: none;
    position: relative;
  }
`;

const HowItWorksContent = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  gap: ${({ theme }) => theme.spacing(4)}px;
  flex-direction: column;
  text-align: center;

  ${(props) => props.theme.breakpoints.up('sm')} {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(0)}px;
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(4)}px;
  }
`;

const HowItWorksItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing(2)}px;

  ${(props) => props.theme.breakpoints.up('sm')} {
    flex-basis: 0;
    flex-grow: 1;
    flex-shrink: 0;
    gap: ${({ theme }) => theme.spacing(0)}px;
  }
`;

export default HowItWorksCTA;
